/**
 * CustomTemplateCanvas — the custom HTML template, shown in All Journeys' EXISTING right-hand
 * preview pane, plus the small pieces of header, toolbar and settings-pane context that say
 * which design is on screen.
 *
 * The pane's own toolbar keeps its device toggle and zoom; this canvas reads them, so there
 * is one set of controls, never a second nested one. The page is the portal composer's
 * output for the chosen published template and the chosen real event, read through
 * previewPortalExperiencePage by ./template-preview-controller. It is drawn in a sandboxed
 * frame with every permission withheld (no scripts, forms, same-origin access, popups or
 * top navigation). A device wider than the pane starts at the pane's left edge, so all of it
 * can be scrolled to.
 *
 * A preview is never presented as live: a pick that is not applied is labelled so, and the
 * only way to change what visitors see is Apply in the template settings.
 */
import React from "react";
import { Banner, Button, InlineEmptyState, SegmentedControl, Skeleton, Spinner, StatusPill } from "@v0y4ge/jrni-ui";
import { t as i18nT } from "../../../lib/i18n";
import { tf } from "./template-data";
import {
  INHERIT_VALUE, useTemplatePreviewController,
  type TemplatePreviewController, type TemplatePreviewView,
} from "./template-preview-controller";

type Device = "desktop" | "tablet" | "mobile";

function useView(controller: TemplatePreviewController): TemplatePreviewView {
  useTemplatePreviewController(controller);
  return controller.view();
}

/**
 * What visitors are actually served in this context, in words. A template that is assigned
 * but not published right now is NOT what they see: they get the fallback.
 */
export function visitorsSeeLabel(view: TemplatePreviewView): string {
  const sel = view.selection;
  if (sel && sel.inUseSlug && sel.inUseAvailable) return view.templateTitle(sel.inUseSlug) || sel.inUseSlug;
  if (sel && sel.tab === "events") return i18nT("journeys.customTemplates.standardList", "Standard event list");
  return sel && sel.inUseSlug
    ? i18nT("journeys.customTemplates.fallbackPage", "The fallback page")
    : i18nT("journeys.customTemplates.standardEventPage", "The standard event page");
}

function shownName(view: TemplatePreviewView): string {
  const slug = view.selection?.effectiveSlug ?? null;
  return view.templateTitle(slug) || slug || "";
}

/* ── Toolbar: Standard design / Custom template ─────────────────────────── */

export function TemplateDesignSwitch(props: { controller: TemplatePreviewController }) {
  const view = useView(props.controller);
  const sel = view.selection;
  if (!sel || !(view.customAvailable || sel.inUseSlug)) return null;
  return (
    <SegmentedControl
      size="sm"
      ariaLabel={i18nT("journeys.customTemplates.designShown", "Design shown in the preview")}
      options={[
        { value: "standard", label: i18nT("journeys.customTemplates.standardDesign", "Standard design") },
        { value: "custom", label: i18nT("journeys.customTemplates.customTemplate", "Custom template"), disabled: !view.customAvailable },
      ]}
      value={view.mode}
      onChange={(v) => props.controller.setView(v === "custom" ? "custom" : "standard")}
    />
  );
}

/** The preview toolbar's title while the custom template is shown (in place of the step name). */
export function TemplateCanvasTitle(props: { controller: TemplatePreviewController }) {
  const view = useView(props.controller);
  const sel = view.selection;
  if (!sel) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
      <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--jrni-color-text-strong)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "260px" }}>
        {shownName(view)}
      </span>
      <StatusPill size="sm" tone={view.notPublished || sel.candidate ? "warning" : "primary"}>
        {view.notPublished
          ? i18nT("journeys.customTemplates.notPublishedShort", "Not published")
          : sel.candidate
            ? i18nT("journeys.customTemplates.notApplied", "Not applied")
            : i18nT("journeys.customTemplates.inUseShort", "In use")}
      </StatusPill>
    </span>
  );
}

/**
 * The header status while the custom template is shown (in place of the standard design's status).
 * The chip never grows past its parent (at most 320px): the native wrapper and the pill both
 * shrink, and the pill lays its dot and text out on a grid whose text column may shrink to zero,
 * so a long status ends in an ellipsis inside the pill instead of being cut off by the page. The
 * full text stays in the DOM, is the pill's accessible name, and is the wrapper's hover title.
 */
export function TemplateHeaderStatus(props: { controller: TemplatePreviewController }) {
  const view = useView(props.controller);
  const sel = view.selection;
  if (!sel) return null;
  const text = view.notPublished
    ? tf("journeys.customTemplates.headerUnavailable", "{name} isn't published. Visitors see: {fallback}", { name: shownName(view), fallback: visitorsSeeLabel(view) })
    : sel.candidate
      ? tf("journeys.customTemplates.headerPreviewing", "Previewing {name}, not applied", { name: shownName(view) })
      : tf("journeys.customTemplates.headerInUse", "Custom template in use: {name}", { name: shownName(view) });
  return (
    <span title={text} style={{ display: "inline-flex", minWidth: 0, maxWidth: "min(320px, 100%)", overflow: "hidden" }}>
      <StatusPill dot tone={view.notPublished || sel.candidate ? "warning" : "primary"} aria-label={text}
        style={{ display: "inline-grid", gridTemplateColumns: "auto minmax(0, 1fr)", alignItems: "center", minWidth: 0, maxWidth: "100%", boxSizing: "border-box", overflow: "hidden" }}>
        <span style={{ display: "block", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{text}</span>
      </StatusPill>
    </span>
  );
}

/* ── Settings pane context ──────────────────────────────────────────────── */

/** Above the settings while the custom template is shown: these controls edit the standard design. */
export function CustomTemplateNotice(props: { controller: TemplatePreviewController }) {
  const view = useView(props.controller);
  const sel = view.selection;
  if (!sel || view.mode !== "custom") return null;
  return (
    <div style={{ maxWidth: "880px", marginBottom: "16px" }}>
      <Banner tone="info" size="compact"
        title={view.notPublished
          ? tf("journeys.customTemplates.noticeUnavailable", "“{name}” isn't published right now. Visitors see: {fallback}", { name: shownName(view), fallback: visitorsSeeLabel(view) })
          : sel.candidate
            ? tf("journeys.customTemplates.noticePreviewing", "The preview shows “{name}”, which isn't applied", { name: shownName(view) })
            : tf("journeys.customTemplates.noticeInUse", "Visitors see the custom template “{name}”", { name: shownName(view) })}
        action={<Button size="sm" hierarchy="secondary" onClick={() => props.controller.setView("standard")}>{i18nT("journeys.customTemplates.editStandard", "Edit standard design")}</Button>}>
        {i18nT("journeys.customTemplates.noticeStandardScope", "The settings on this tab change the standard design, not the template. The standard design stays saved, with any unsaved changes, while the template is shown.")}
      </Banner>
    </div>
  );
}

/**
 * Above the standard designer's canvas: says so when a custom template is what visitors see,
 * and when it could not be checked, rather than silently showing the standard design.
 */
export function TemplateCheckNotice(props: { controller: TemplatePreviewController }) {
  const view = useView(props.controller);
  const state = props.controller.getState();
  const sel = view.selection;
  if (!sel || view.mode !== "standard") return null;
  if (state.data.status === "error" && !state.settings) {
    return (
      <div style={{ padding: "12px 12px 0" }}>
        <Banner tone="danger" size="compact" title={i18nT("journeys.customTemplates.checkFailed", "Couldn't check whether a custom template is in use")}
          action={<Button size="sm" hierarchy="secondary" onClick={props.controller.refresh}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button>}>
          {state.data.message}
        </Banner>
      </div>
    );
  }
  if (!sel.inUseSlug) return null;
  const assigned = view.templateTitle(sel.inUseSlug) || sel.inUseSlug;
  if (!sel.inUseAvailable) {
    return (
      <div style={{ padding: "12px 12px 0" }}>
        <Banner tone="warning" size="compact"
          title={tf("journeys.customTemplates.standardWhileUnavailable", "“{name}” is chosen here but isn't published right now. Visitors see: {fallback}", { name: assigned, fallback: visitorsSeeLabel(view) })} />
      </div>
    );
  }
  return (
    <div style={{ padding: "12px 12px 0" }}>
      <Banner tone="info" size="compact"
        title={tf("journeys.customTemplates.standardWhileCustom", "Visitors see the custom template “{name}”", { name: assigned })}
        action={view.customAvailable
          ? <Button size="sm" hierarchy="secondary" onClick={() => props.controller.setView("custom")}>{i18nT("journeys.customTemplates.showCustom", "Show custom template")}</Button>
          : undefined}>
        {i18nT("journeys.customTemplates.standardWhileCustomBody", "You're editing the standard design. Saving it doesn't change what visitors see while the template is applied.")}
      </Banner>
    </div>
  );
}

/* ── The canvas ─────────────────────────────────────────────────────────── */

export interface CustomTemplateCanvasProps {
  controller: TemplatePreviewController;
  /** The pane toolbar's device and zoom: the same controls the standard designer uses. */
  device: Device;
  widthPx: number;
  heightPx: number;
  zoom: number;
  /** Open the tab's General settings, where the template is chosen and applied. */
  onGoToSettings: () => void;
}

export function CustomTemplateCanvas(props: CustomTemplateCanvasProps) {
  const view = useView(props.controller);
  const state = props.controller.getState();
  const sel = view.selection;
  const p = view.preview;
  const zoom = Math.max(50, Math.min(150, props.zoom || 100)) / 100;
  const desktop = props.device === "desktop" || !props.widthPx;
  const html = p.html;
  const name = shownName(view);

  const notes: React.ReactNode[] = [];
  if (sel && sel.candidate) {
    notes.push(
      <Banner key="candidate" tone="warning" size="compact"
        title={i18nT("journeys.customTemplates.previewNotApplied", "Preview only: not applied")}
        action={<Button size="sm" hierarchy="secondary" onClick={props.onGoToSettings}>{i18nT("journeys.customTemplates.goToSettings", "Go to template settings")}</Button>}>
        {tf("journeys.customTemplates.previewNotAppliedBody", "Visitors still see {current}. Apply this choice in the template settings to make it live.", { current: visitorsSeeLabel(view) })}
      </Banner>,
    );
  }
  if (sel && sel.event && sel.eventRole) {
    notes.push(
      <span key="event" style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>
        {sel.eventRole === "exemplar"
          ? tf("journeys.customTemplates.shownWithExample", "Shown with {event} as an example event", { event: sel.event.name })
          : sel.value === INHERIT_VALUE
            ? tf("journeys.customTemplates.shownWithInherited", "Shown with {event}, which uses the default for all events", { event: sel.event.name })
            : tf("journeys.customTemplates.previewWith", "Shown with {event}", { event: sel.event.name })}
        {sel.event.when ? " · " + sel.event.when : ""}
      </span>,
    );
  }
  if (sel && sel.eventRole === "exemplar" && sel.exemplarOverride && sel.event) {
    notes.push(
      <Banner key="override" tone="neutral" size="compact" title={i18nT("journeys.customTemplates.exemplarOwnTitle", "This event has its own template")}>
        {tf("journeys.customTemplates.exemplarOwnBody", "{event} uses “{name}”, which a default never replaces. Its live page shows that template; this preview shows the default you chose.", {
          event: sel.event.name, name: view.templateTitle(sel.exemplarOverride) || sel.exemplarOverride,
        })}
      </Banner>,
    );
  }
  if (state.data.status === "error") {
    notes.push(
      <Banner key="data" tone="danger" size="compact" title={i18nT("journeys.customTemplates.loadFailed", "Templates couldn't be loaded")}
        action={<Button size="sm" hierarchy="secondary" onClick={props.controller.refresh}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button>}>
        {state.data.message}
      </Banner>,
    );
  }

  let body: React.ReactNode = null;
  if (view.notPublished) {
    body = (
      <Banner tone="warning" title={i18nT("journeys.customTemplates.notLiveTitle", "This template isn't published right now")}
        action={<Button size="sm" hierarchy="secondary" onClick={props.controller.refresh}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button>}>
        {sel && sel.tab === "events"
          ? i18nT("journeys.customTemplates.notLiveList", "Visitors see the standard event list until it is published again, or until you choose another template.")
          : i18nT("journeys.customTemplates.notLiveDetail", "Visitors see the fallback page until it is published again, or until you choose another template.")}
      </Banner>
    );
  } else if (p.needsEvent) {
    body = state.catalog.status === "error" ? (
      <Banner tone="danger" title={i18nT("journeys.customTemplates.catalogFailed", "Events couldn't be loaded")}
        action={<Button size="sm" hierarchy="secondary" onClick={props.controller.loadCatalog}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button>}>
        {state.catalog.message}
      </Banner>
    ) : state.catalog.status === "ready" ? (
      <InlineEmptyState title={i18nT("journeys.customTemplates.noEvents", "No published events")}
        description={i18nT("journeys.customTemplates.previewNeedsEvent", "A preview needs a published event.")} />
    ) : (
      <span role="status" style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{i18nT("journeys.customTemplates.loadingEvents", "Loading events…")}</span>
    );
  }

  /* The current answer's findings. A blank answer is reported, never shown as an empty page;
     an earlier page of this same context stays visible, marked stale. */
  const problems = p.diagnostics ? (
    <Banner tone="warning" size="compact" title={i18nT("journeys.customTemplates.previewProblems", "This template has problems")}>
      {p.diagnostics.errors.length
        ? <ul style={{ margin: 0, paddingLeft: "18px" }}>{p.diagnostics.errors.map((f, i) => <li key={i}>{f.message}</li>)}</ul>
        : null}
      {!p.diagnostics.composed ? i18nT("journeys.customTemplates.nothingComposed", "No page could be composed from it.") : null}
    </Banner>
  ) : null;

  /* The device box: real device size times the toolbar's zoom. Desktop fills the pane. */
  const boxStyle: React.CSSProperties = desktop
    ? { position: "relative", flex: "1 0 480px", width: "100%", minWidth: 0 }
    : { position: "relative", flex: "0 0 auto", alignSelf: "flex-start", marginLeft: "auto", marginRight: "auto", width: props.widthPx * zoom + "px", height: (props.heightPx || 720) * zoom + "px" };
  const frameStyle: React.CSSProperties = {
    position: "absolute", top: 0, left: 0, border: 0, background: "#fff",
    width: desktop ? 100 / zoom + "%" : props.widthPx + "px",
    height: desktop ? 100 / zoom + "%" : (props.heightPx || 720) + "px",
    transform: "scale(" + zoom + ")", transformOrigin: "top left",
    borderRadius: "var(--jrni-radius-lg)", boxShadow: "var(--jrni-shadow-lg)",
    opacity: p.stale || p.status === "loading" ? 0.72 : 1, transition: "opacity 120ms ease",
  };

  return (
    <div className="apd-scroll" style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto", padding: "12px", display: "flex", flexDirection: "column", gap: "12px" }}>
      {notes}
      {body}
      {!body && p.status === "error" ? (
        <Banner tone="danger" title={i18nT("journeys.customTemplates.previewFailed", "The preview couldn't be shown")}
          action={<Button size="sm" hierarchy="secondary" onClick={props.controller.retryPreview}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button>}>
          {p.message}
        </Banner>
      ) : null}
      {!body && p.stale && html ? (
        <Banner tone="warning" size="compact">
          {p.status === "loading"
            ? i18nT("journeys.customTemplates.staleLoading", "Showing the last preview of this choice while the new one loads.")
            : i18nT("journeys.customTemplates.staleKept", "Showing the last preview of this choice that loaded. It may be out of date.")}
        </Banner>
      ) : null}
      {!body ? problems : null}
      {!body && p.status === "loading" && html ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "var(--jrni-color-text-muted)" }}>
          <Spinner size="sm" label={i18nT("journeys.customTemplates.updatingPreview", "Updating preview")} />
          {i18nT("journeys.customTemplates.updatingPreview", "Updating preview")}
        </span>
      ) : null}
      {!body && html ? (
        <div style={boxStyle}>
          <iframe
            title={tf("journeys.customTemplates.canvasFrame", "Preview of {name}", { name })}
            sandbox=""
            referrerPolicy="no-referrer"
            srcDoc={html}
            style={frameStyle}
          />
        </div>
      ) : !body && p.status === "loading" ? (
        <div role="status" aria-label={i18nT("journeys.customTemplates.loadingPreview", "Loading preview")}
          style={{ ...boxStyle, display: "flex", flexDirection: "column", gap: "14px", padding: "16px", boxSizing: "border-box", background: "var(--jrni-color-surface-card)", borderRadius: "var(--jrni-radius-lg)" }}>
          <Skeleton variant="rect" width="100%" height={56} />
          <Skeleton variant="rect" width="100%" height={180} />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="55%" />
          <Skeleton variant="rect" width="100%" height={120} />
        </div>
      ) : null}
    </div>
  );
}
